import {
  Outlet,
  createRootRoute,
  HeadContent,
  Scripts,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { lazy, Suspense, useEffect } from "react";
import { cn } from "@/lib/utils";

import appCss from "../styles.css?url";
import { ContactTopBar } from "@/components/ContactTopBar";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { BackToTop } from "@/components/BackToTop";
import { NotFoundPage } from "@/components/NotFoundPage";

const WebsiteChat = lazy(() =>
  import("@/components/WebsiteChat").then((module) => ({ default: module.WebsiteChat })),
);
const AdsCookieConsent = lazy(() =>
  import("@/components/AdsCookieConsent").then((module) => ({
    default: module.AdsCookieConsent,
  })),
);

function NotFoundComponent() {
  return (
    <div className="theme-scope flex min-h-screen flex-col bg-background text-foreground">
      <ContactTopBar />
      <SiteHeader />
      <main className="relative z-[1] flex-1 overflow-hidden isolate">
        <div className="page-decor" aria-hidden="true">
          <span className="shape-1" />
          <span className="shape-2" />
          <span className="shape-3" />
          <span className="shape-4" />
        </div>
        <NotFoundPage />
      </main>
      <SiteFooter />
      <BackToTop />
      <Suspense fallback={null}>
        <WebsiteChat />
      </Suspense>
    </div>
  );
}

export const Route = createRootRoute({
  beforeLoad: ({ location }) => {
    if (location.pathname.length > 1 && location.pathname.endsWith("/")) {
      throw redirect({
        href: `${location.pathname.replace(/\/+$/, "")}${location.searchStr}${location.hash ? `#${location.hash}` : ""}`,
        statusCode: 301,
      });
    }
  },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { httpEquiv: "X-UA-Compatible", content: "IE=edge" },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      { name: "author", content: "Phil Carr" },
      { property: "og:type", content: "website" },
      {
        property: "og:site_name",
        content: "Phil Carr SEO Consultant & Web Strategist",
      },
      {
        property: "og:image",
        content: "https://www.phil-carr.co.uk/images/hero-home-generated.jpg",
      },
      {
        property: "og:image:width",
        content: "1600",
      },
      {
        property: "og:image:height",
        content: "1000",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@philcarrseo" },
      {
        name: "twitter:image",
        content: "https://www.phil-carr.co.uk/images/hero-home-generated.jpg",
      },
      { name: "theme-color", content: "#171717" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      {
        rel: "preload",
        as: "image",
        href: "/images/page-header-bg.webp",
        type: "image/webp",
        fetchPriority: "high",
      },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/images/favicon-32x32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/images/favicon-16x16.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/images/apple-touch-icon.png" },
    ],
    scripts: [
      {
        src: "/assets/menu-fallback.js",
        defer: true,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://www.phil-carr.co.uk/#organization",
          name: "Phil Carr SEO Consultant & Web Strategist",
          url: "https://www.phil-carr.co.uk/",
          logo: "https://www.phil-carr.co.uk/images/phil-carr-logo.svg",
          founder: { "@id": "https://www.phil-carr.co.uk/#person" },
          sameAs: ["https://uk.linkedin.com/in/phil-carr-seo"],
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "+44 1226 697325",
            contactType: "customer service",
            areaServed: "GB",
            availableLanguage: "en-GB",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          "@id": "https://www.phil-carr.co.uk/#person",
          name: "Phil Carr",
          url: "https://www.phil-carr.co.uk/about",
          jobTitle: "SEO Consultant & Web Strategist",
          description:
            "Barnsley-based SEO consultant and web strategist with 12+ years of practical experience in technical SEO, local SEO, content strategy, analytics, and conversion-led web design.",
          knowsAbout: [
            "Technical SEO",
            "Local SEO",
            "Content strategy",
            "Google Analytics",
            "Google Ads",
            "Schema markup",
            "Web design",
            "Conversion strategy",
          ],
          worksFor: { "@id": "https://www.phil-carr.co.uk/#organization" },
          sameAs: ["https://uk.linkedin.com/in/phil-carr-seo"],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://www.phil-carr.co.uk/#website",
          url: "https://www.phil-carr.co.uk/",
          name: "Phil Carr SEO Consultant & Web Strategist",
          publisher: { "@id": "https://www.phil-carr.co.uk/#organization" },
          inLanguage: "en-GB",
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en-GB">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const router = useRouter();
  const isContact = router.state.location.pathname === "/contact";

  useEffect(() => {
    document.documentElement.dataset.appHydrated = "true";
    return () => {
      delete document.documentElement.dataset.appHydrated;
    };
  }, []);

  return (
    <div className="theme-scope flex min-h-screen flex-col bg-background text-foreground">
      <ContactTopBar />
      <SiteHeader />
      <main
        className={cn("relative z-[1] flex-1 overflow-hidden isolate", isContact && "no-decor")}
      >
        <div className="page-decor" aria-hidden="true">
          <span className="shape-1" />
          <span className="shape-2" />
          <span className="shape-3" />
          <span className="shape-4" />
        </div>
        <Outlet />
      </main>
      <SiteFooter />
      <BackToTop />
      <Suspense fallback={null}>
        <WebsiteChat />
        <AdsCookieConsent />
      </Suspense>
    </div>
  );
}
